import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost } from "../../services/httpService";
import { findPharmacyDisposition } from "../../config/iuc/pharmacy-disposition-codes";
import { v4 as uuidv4 } from "uuid";
import {
  getPharmacyServiceUrl,
  getPharmacyClinicalServiceUrl,
  getPharmacyClinicalServiceCountUrl,
  getClinicalServiceBuilderByServiceIdUrl,
  getClinicalServiceByCompanyAndServiceIdUrl,
  getEventsUrl,
  updateEventUrl,
  updateCalendarEventByClinicalServiceIdUrl,
  updateRecheduleByClinicalServiceIdUrl,
  getAnalyticsByServicePharmacyDateUrl,
  getPharmaciesByCompanyIdUrl,
  getPharmacyByPharmacyIdUrl,
  addpharmacyServiceUrl,
  sendSMSUrl,
  getSMSUrl,
  getOrbitSessionDetailUrl,
  saveOrbitSessionDetailUrl,
  getUserLogUrl,
  savePatientContactUrl,
  getIdentityVerificationUrl,
  getIdentityVerificationDataBySessionIdUrl,
  checkYotiDataBySessionIdUrl,
  getServiceProgressURL,
  updateClaimStatusURL,
  sendToOrbitURL,
  changeSentToOrbitUrl,
  moveInterventionUrl,
  declineServicesUrl,
  rescheduleServicesUrl,
  getPharmacyActionsUrl,
  changePharmacyActionsUrl,
  getTitanPatientPdsUrl,
  moveDeclineServiceToClinicalServiceUrl,
  getDefaultCompanyServiceUrl,
  duplicateServiceUrl,
  getDefaultServicesByCompanyIdAndPharmacyIdUrl,
  getClinicalServiceBuilderByIdUrl,
  getServiceTemplatesUrl,
  submitCustomTemplateUrl,
  getClinicalServiceBuilderByCompanyIdUrl,
  updateClinicalServicePaymentDetailsUrl,
  getAgencyRatesUrl,
  getAgencyPharmacyUrl,
  addAgencyPharmacyUrl,
  getAnalyticsNMSProcessedUrl,
  getAnalyticsServicesEarningsUrl,
  getAnalyticsServicesUsersUrl,
  getAnalyticsServicesStagesUrl,
  getAnalyticsServicesCompletedClaimedUrl,
  getAnalyticsServicesCompletedToBeClaimedUrl,
  getAnalyticsServicesDeclinedUrl,
  sendToAgencyOrbitURL,
  getDoseShortCodeListUrl,
  UpdatePharmacyLocationUrl,
  getRoomsUrl,
  getUsersByServiceIDUrl,
  getUsersByServiceCompanyPharmacyIDUrl,
  getAvailablePharmacistsForServiceUrl,
  getPharmacyAvailableSlotsUrl,
  getActivePharmacistsUrl,
  getAnalyticsServicesCompletedToBeClaimedHisotryUrl,
  getHistoryAnalyticsNMSProcessedUrl,
  getHistoryAnalyticsServicesCompletedClaimedUrl,
  getConsultationForNHSEmergencyServiceUrl,
  getRoomsForAppointmentUrl,
  getPharmacyContactDetailsUrl,
  getPatientForNHSEmergencyServiceUrl,
  updateConsultationForPatientMatchUrl,
  updateConsultationStageUrl,
  getDefaultCompanyCheckListForPublicURL,
  getQRCheckListForService,
  getReferralDataByConsultationIdUrl,
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";
import moment from "moment";
import { getDueName } from "../../data/getStartDateForStages";

// Bulk endpoints that take a top-level array accept at most 1000 items per
// request (API schema maxItems), so larger selections are sent in batches.
const MAX_ITEMS_PER_REQUEST = 1000;

const postInBatches = async (url, items, combineResults) => {
  if (!Array.isArray(items) || items.length <= MAX_ITEMS_PER_REQUEST) {
    return httpPost(url, items);
  }
  const results = [];
  for (let i = 0; i < items.length; i += MAX_ITEMS_PER_REQUEST) {
    results.push(
      await httpPost(url, items.slice(i, i + MAX_ITEMS_PER_REQUEST)),
    );
  }
  return combineResults(results);
};

const allSucceeded = (results) => results.every(Boolean);
const concatLists = (results) => results.flatMap((result) => result || []);

const tzoffset = new Date().getTimezoneOffset() * 60000;
const defaultStartDate = new Date(Date.now() - tzoffset);
defaultStartDate.setMonth(defaultStartDate.getMonth() - 1);
const defaultEndDate = new Date(Date.now() - tzoffset);
const persistedFilters = (() => {
  try {
    return JSON.parse(localStorage.getItem("serviceFilters")) || {};
  } catch {
    return {};
  }
})();

const persistedSort = (() => {
  try {
    return JSON.parse(localStorage.getItem("serviceStageSortOrders")) || {};
  } catch {
    return {};
  }
})();

const saveSortToStorage = (stageSortOrders) => {
  localStorage.setItem(
    "serviceStageSortOrders",
    JSON.stringify(stageSortOrders),
  );
};

// Sorts by date per patient stack, not per clinical service: a patient's
// services move together as a block keyed by their front (first) item's date,
// and keep their default relative order inside the stack. Sorting each item
// individually reshuffled stacks and put a different clinical service in front.
const sortClinicalServicesByPatient = (clinicalServices, getItemDate, ascending) => {
  const items = clinicalServices ?? [];
  const patientKeys = new Map();
  items.forEach((cs, idx) => {
    if (!patientKeys.has(cs?.patientId)) {
      patientKeys.set(cs?.patientId, { time: getItemDate(cs), idx });
    }
  });
  return [...items].sort((a, b) => {
    if (a?.patientId === b?.patientId) return 0;
    const ka = patientKeys.get(a?.patientId);
    const kb = patientKeys.get(b?.patientId);
    const diff = ascending ? ka.time - kb.time : kb.time - ka.time;
    return diff || ka.idx - kb.idx;
  });
};

const saveFiltersToStorage = (state) => {
  const filtersToSave = {
    patientName: state.patientName,
    filterNmsValue: state.filterNmsValue,
    filterTagValues: state.filterTagValues,
    filterTagValuesConsented: state.filterTagValuesConsented,
  };

  localStorage.setItem("serviceFilters", JSON.stringify(filtersToSave));
};

const initialState = {
  isPharmacyServiceLoading: false,
  isPharmacyServiceSiedebarLoading: false,
  isPharmacyAnalyticsLoading: false,
  isCalenderShow: false,
  isServiceProgressLoading: false,
  newClinicComponent: false,
  currentServiceId: "0",
  currentServiceTab: 0,
  NhsCount: 0,
  privateCount: 0,
  totalCount: 0,
  serviceName: "",
  serviceIcon: "",
  serviceDuplicated: false,
  serviceBuilderType: "0",
  ClinicalServiceBuilder: [],
  pharmacyServices: [],
  pharmacyClincalServices: [],
  pharmacyClincalServicesCount: [],
  currentServiceData: [],
  clinicalServiceBuilderModel: [],
  currentEvents: [],
  serviceAnalytics: [],
  calendarStartDate: defaultStartDate.toISOString(),
  calendarEndDate: defaultEndDate.toISOString(),
  allPharmacies: [],
  currentPharmacyService: null,
  smsQueue: [],
  smsUrls: [],
  orbitSessionDetail: null,
  orbitSession: null,
  userLog: null,
  isNmsProgress: false,
  userName: "",
  savePatientContact: null,
  isPatientContact: false,
  contactServiceId: "",
  filterPharmacyService: [],
  filterNmsValue: persistedFilters.filterNmsValue || "Is",
  filterTagValues: persistedFilters.filterTagValues || [],
  filterTagValuesConsented: persistedFilters.filterTagValuesConsented || [],
  filterNameValues: "",
  isFilterModal: false,
  patientTags: [],
  patientTagsColor: [],
  pharmacyServicesSms: [],
  yotiSessionDetail: null,
  updatedEvent: null,
  scrollPosition: 0,
  open: false,
  analyticsTabValue: 0,
  serviceProgress: 0,
  isSentClaim: false,
  orbitQueue: [],
  sidebarVisible: false,
  selectedRowData: null,
  completedServiceTabValue: 0,
  isYotiSuccess: false,
  isSentToOrbit: false,
  orbitClinicalServiceId: "",
  interventionQueue: [],
  declineQueue: [],
  rescheduleQueue: [],
  pdsQueue: [],
  patientName: persistedFilters.patientName || "Tag",
  interventionStageScrollPosition: 0,
  eligibilityStageScrollPosition: 0,
  listViewGridScrollPosition: 0,
  showReschedule: false,
  checkedAllEligible: false,
  checkedAllConsent: false,
  checkedAllIntervention: false,
  checkedAllFollowUp: false,
  processedPDSServices: [],
  drugFilterValue: "",
  sendSMSAutomation: false,
  sendOrbitAutomation: false,
  sendInterventionAutomation: false,
  sendConsentOnly: false,
  transformedPharmacyData: [],
  pharmacyActions: null,
  originalPharmacyServices: [],
  editedPreferredContact: "",
  sendToInterventionOptions: null,
  isRevertBackToClinicalService: false,
  SendToInterventionDropdownOptions: [
    {
      _id: 0,
      optionName: "With Consent only",
    },
    {
      _id: 1,
      optionName: "For all NMS",
    },
  ],
  isAutomationMenuOpen: false,
  defaultPharmacyServices: [],
  defaultCalenderAppointmentPharmacyServices: [],
  serviceDetails: null,
  serviceDetailTemplates: [],
  customTemplateModel: [],
  isAnyControlChecked: false,
  serviceBuilderDetails: null,
  companyServices: [],
  selectedClinicalServiceId: "",
  showPaymentModal: false,
  showConsultationPage: false,
  isOrbitSendModal: false,
  isDeclineModal: false,
  agencyRates: [],
  isAgencyRatesLoading: false,
  IsPharmacyAgency: false,
  agencyPharmacy: null,
  orbitModalQueue: [],
  declineModalQueue: [],
  isDeclineServiceLoading: false,
  // optimize analytics tab
  isPharmacyServiceProcessedLoading: false,
  isPharmacyServiceEarningLoading: false,
  isPharmacyServiceUsersLoading: false,
  isPharmacyServiceStagesLoading: false,
  isPharmacyServiceCompletedClaimedLoading: false,
  isPharmacyServiceCompletedTobeClaimedLoading: false,
  isPharmacyServiceCompletedTobeClaimedHistoryLoading: false,
  isPharmacyServiceCompletedDeclinedLoading: false,
  serviceAnalyticsProcessed: [],
  serviceHistoricalAnalyticsProcessed: [],
  serviceAnalyticsEarning: [],
  serviceAnalyticsUsers: [],
  serviceAnalyticsStages: [],
  serviceAnalyticsCompletedClaimed: [],
  serviceHistoryAnalyticsCompletedClaimed: [],
  serviceAnalyticsCompletedTobeClaimed: [],
  serviceAnalyticsCompletedTobeClaimedHistorical: [],
  serviceAnalyticsCompletedDeclined: [],
  serviceType: 1,
  currentPharmacyServiceId: "",
  currentCompletedServiceValue: "1",
  exclusivePartner: {},
  patientList: [],
  patientListLoader: false,
  addPatientLoader: false,
  doseShortCodeList: [],
  isUpdatePharmacyLocationLoading: false,
  updatedPharmacy: null,
  roomsData: [],
  isRoomLoading: false,
  serviceUsersByCompany: [],
  // Rich per-service user list (with per-service weekly availability) from
  // get-users-by-serviceID-companyID-pharmacyID. Kept separate from
  // serviceUsersByCompany because getActivePharmacistsData overwrites that
  // field with a leaner list (isAlreadyBooked/isOutsideWorkingHours flags
  // only, no availability), which would otherwise clobber it.
  serviceUsersDetailsByCompany: [],
  serviceRoomsByCompany: [],
  pharmacyOpeningHours: [],
  pharmacyAvailableSlots: [],
  pharmacyAvailableSlotsDate: "",
  showNHSEmergencyKanban: false,
  isNHSEmergencyServiceLoading: false,
  NHSEmergencyServiceData: [],
  rawNHSEmergencyData: [],
  pharmacyDetails: [],
  appointmentRooms: [],
  isAppointmentRoomsLoading: false,
  pharmacyContactDetails: null,
  isNHSEmergencyPatientLoading: false,
  NHSEmergencyPatientData: [],
  matchDialogOpen: false,
  matchPatient: "",
  updateConsultationForPatientMatchLoading: false,
  updateConsultationForPatientMatchResp: [],

  isUpdateConsultationStageLoading: false,
  stageSortOrders: persistedSort,

  showNHS111ReferralTab: false,
  isNHS111ReferralDataLoading: false,
  nhs111ReferralData: null,
  isServiceUsersByCompanyLoading: false,
};

export const getClinicalServiceBuilderByCompanyIdData = createAsyncThunk(
  "pharmacyService/getClinicalServiceBuilderByCompanyIdData",
  async (companyId, thunkApi) => {
    const urlToGet = getClinicalServiceBuilderByCompanyIdUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getPharmacyServiceData = createAsyncThunk(
  "pharmacyService/getPharmacyServiceData",
  async (params) => {
    const { pharmacyId, searchType, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };
    const urlToGet = getPharmacyServiceUrl(pharmacyId, serviceId, searchType);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getPharmacyDefaultCheckListData = createAsyncThunk(
  "Service/GetDefaultCompanyCheckList",
  async (companyId) => {
    const urlToGet = getDefaultCompanyCheckListForPublicURL(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getServiceQRCheckListData = createAsyncThunk(
  "Service/GetQRServiceCheckList",
  async ({ companyId, serviceId, pharmacyId }) => {
    const urlToGet = getQRCheckListForService(companyId, serviceId, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const duplicateServiceData = createAsyncThunk(
  "Service/duplicateServiceData",
  async ({ serviceId, companyId }) => {
    const urlToGet = duplicateServiceUrl(serviceId, companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getPharmacyClinicalServiceData = createAsyncThunk(
  "pharmacyService/getPharmacyClinicalServiceData",
  async (pharmacyId) => {
    const urlToGet = getPharmacyClinicalServiceUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getPharmacyClinicalServiceCountData = createAsyncThunk(
  "pharmacyService/getPharmacyClinicalServiceCountData",
  async (pharmacyId) => {
    const urlToGet = getPharmacyClinicalServiceCountUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getClinicalServiceBuilderByServiceIdData = createAsyncThunk(
  "pharmacyService/getClinicalServiceBuilderByServiceIdData",
  async (params) => {
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };
    const urlToGet = getClinicalServiceBuilderByServiceIdUrl(
      pharmacyId,
      serviceId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getClinicalServiceByCompanyAndServiceIdData = createAsyncThunk(
  "pharmacyService/getClinicalServiceByCompanyAndServiceIdData",
  async (params) => {
    const { companyID, serviceID } = params || {
      companyID: "00000000-0000-0000-0000-000000000000",
    };
    const urlToGet = getClinicalServiceByCompanyAndServiceIdUrl(
      companyID,
      serviceID,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getEventsData = createAsyncThunk(
  "pharmacyService/getEventsData",
  async (params) => {
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };
    const urlToGet = getEventsUrl(pharmacyId, serviceId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsByServicePharmacyDateData = createAsyncThunk(
  "pharmacyService/getAnalyticsByServicePharmacyDateData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsByServicePharmacyDateUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getPharmaciesByCompanyIdData = createAsyncThunk(
  "pharmacyService/getPharmaciesByCompanyIdData",
  async (companyId, thunkApi) => {
    const urlToGet = getPharmaciesByCompanyIdUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getPharmacyByPharmacyIdData = createAsyncThunk(
  "pharmacyService/getPharmacyByPharmacyIdData",
  async (pharmacyId, thunkApi) => {
    const urlToGet = getPharmacyByPharmacyIdUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const addpharmacyServiceData = createAsyncThunk(
  "pharmacyService/addpharmacyServiceData",
  async ({ pharmacyId, data }, thunkApi) => {
    const url = addpharmacyServiceUrl(pharmacyId);
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const updateEventData = createAsyncThunk(
  "pharmacyService/updateEventData",
  async (data, thunkApi) => {
    const url = updateEventUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const updateCalendarEventByClinicalServiceIdData = createAsyncThunk(
  "pharmacyService/updateCalendarEventByClinicalServiceIdData",
  async (params, thunkApi) => {
    const { formattedDateTime, clinicalServiceId, stageNo } = params || {
      formattedDateTime: "",
    };
    const url = updateCalendarEventByClinicalServiceIdUrl(
      formattedDateTime,
      clinicalServiceId,
      stageNo,
    );
    const respData = await httpGet(url);
    return respData;
  },
);
export const updateRecheduleByClinicalServiceIdData = createAsyncThunk(
  "pharmacyService/updateRecheduleByClinicalServiceIdData",
  async (params, thunkApi) => {
    const { formattedDateTime, clinicalServiceId, stageNo, data } = params || {
      formattedDateTime: "",
    };
    const url = updateRecheduleByClinicalServiceIdUrl(
      formattedDateTime,
      clinicalServiceId,
      stageNo,
    );
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const sendSMSData = createAsyncThunk(
  "pharmacyService/sendSMSData",
  async (data, thunkApi) => {
    const url = sendSMSUrl();

    const respData = await httpPost(url, data);
    return respData;
  },
);
export const updateClaimStatusData = createAsyncThunk(
  "pharmacyService/updateClaimStatusData",
  async (data, thunkApi) => {
    const url = updateClaimStatusURL();
    const respData = await postInBatches(url, data, allSucceeded);
    return respData;
  },
);
export const sendToOrbitData = createAsyncThunk(
  "pharmacyService/sendToOrbitData",
  async (data, thunkApi) => {
    const url = sendToOrbitURL();
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const sendToAgencyOrbitData = createAsyncThunk(
  "pharmacyService/sendToAgencyOrbitData",
  async (data, thunkApi) => {
    const url = sendToAgencyOrbitURL();
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const changeSentToOrbitData = createAsyncThunk(
  "pharmacyService/changeSentToOrbitData",
  async (serviceId, thunkApi) => {
    const urlToGet = changeSentToOrbitUrl(serviceId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getSMSData = createAsyncThunk(
  "pharmacyService/getSMSData",
  async (thunkApi) => {
    const urlToGet = getSMSUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getIdentityVerificationData = createAsyncThunk(
  "pharmacyService/getIdentityVerificationData",
  async (thunkApi) => {
    const urlToGet = getIdentityVerificationUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getIdentityVerificationDataBySessionIdData = createAsyncThunk(
  "pharmacyService/getIdentityVerificationDataBySessionIdData",
  async (params, thunkApi) => {
    const { sessionId, gphcNumber } = params || {};
    const urlToGet = getIdentityVerificationDataBySessionIdUrl(
      sessionId,
      gphcNumber,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const checkYotiDataBySessionIdData = createAsyncThunk(
  "pharmacyService/checkYotiDataBySessionIdData",
  async (params, thunkApi) => {
    const urlToGet = checkYotiDataBySessionIdUrl(params);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getOrbitSessionDetailData = createAsyncThunk(
  "pharmacyService/getOrbitSessionDetailData",
  async (pharmacyId, thunkApi) => {
    const urlToGet = getOrbitSessionDetailUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const saveOrbitSessionDetailData = createAsyncThunk(
  "pharmacyService/saveOrbitSessionDetailData",
  async (data, thunkApi) => {
    const url = saveOrbitSessionDetailUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const getUserLogData = createAsyncThunk(
  "pharmacyService/getUserLogData",
  async (serviceId, thunkApi) => {
    const urlToGet = getUserLogUrl(serviceId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const savePatientContactData = createAsyncThunk(
  "pharmacyService/savePatientContactData",
  async (params, thunkApi) => {
    const { serviceId, telephone, data } = params || {};
    const url = savePatientContactUrl(serviceId, telephone);
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const getServiceProgressData = createAsyncThunk(
  "pharmacyService/getServiceProgressData",
  async (params) => {
    const { serviceId, pharmacyId } = params;
    const urlToGet = getServiceProgressURL(serviceId, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getPharmacyActionsData = createAsyncThunk(
  "pharmacyService/getPharmacyActionsData",
  async (params) => {
    const urlToGet = getPharmacyActionsUrl(params);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const changePharmacyActionsData = createAsyncThunk(
  "pharmacyService/changePharmacyActionsData",
  async (params) => {
    const { pharmacyId, pharmacyActionType, isAction } = params;
    const urlToGet = changePharmacyActionsUrl(
      pharmacyId,
      pharmacyActionType,
      isAction,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const moveInterventionData = createAsyncThunk(
  "pharmacyService/moveInterventionData",
  async (data, thunkApi) => {
    const url = moveInterventionUrl();
    const respData = await postInBatches(url, data, allSucceeded);
    return respData;
  },
);
export const declineServicesData = createAsyncThunk(
  "pharmacyService/declineServicesData",
  async (data, thunkApi) => {
    const url = declineServicesUrl();
    const respData = await postInBatches(url, data, allSucceeded);
    return respData;
  },
);
export const moveDeclineServiceToClinicalServiceData = createAsyncThunk(
  "pharmacyService/moveDeclineServiceToClinicalServiceData",
  async (clinicalServiceId) => {
    const url = moveDeclineServiceToClinicalServiceUrl(clinicalServiceId);
    const respData = await httpGet(url);
    return respData;
  },
);
export const rescheduleServicesData = createAsyncThunk(
  "pharmacyService/rescheduleServicesData",
  async (params, thunkApi) => {
    const { data, rescheduleDate } = params;
    const url = rescheduleServicesUrl(rescheduleDate);
    const respData = await postInBatches(url, data, allSucceeded);
    return respData;
  },
);
export const getServiceTitanPatientPdsData = createAsyncThunk(
  "pharmacyService/getTitanPatientPdsData",
  async (params, thunkAPI) => {
    const { pharmacyID, clinicalServiceId } = params || {};

    const respData = await httpGet(
      getTitanPatientPdsUrl(pharmacyID, clinicalServiceId),
    );
    return respData;
  },
);
export const getDefaultCompanyServiceUrlData = createAsyncThunk(
  "pharmacyService/getDefaultCompanyServiceUrlData",
  async (companyId, thunkApi) => {
    const urlToGet = getDefaultCompanyServiceUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getDefaultServicesByCompanyIdAndPharmacyIdData = createAsyncThunk(
  "pharmacyService/getDefaultServicesByCompanyIdAndPharmacyIdData",
  async (params, thunkApi) => {
    const { companyId, pharmacyId, appointmentDate } = params || {};
    const urlToGet = getDefaultServicesByCompanyIdAndPharmacyIdUrl(
      companyId,
      pharmacyId,
      appointmentDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getClinicalServiceBuilderByIdData = createAsyncThunk(
  "pharmacyService/getClinicalServiceBuilderByIdData",
  async (serviceId) => {
    const urlToGet = getClinicalServiceBuilderByIdUrl(serviceId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getServiceTemplatesData = createAsyncThunk(
  "pharmacyService/getServiceTemplatesData",
  async (data, thunkApi) => {
    const url = getServiceTemplatesUrl();
    const respData = await postInBatches(url, data, concatLists);
    return respData;
  },
);
export const getAgencyRatesData = createAsyncThunk(
  "pharmacyService/getAgencyRatesData",
  async (data, thunkApi) => {
    const url = getAgencyRatesUrl();
    const respData = await httpGet(url);
    return respData;
  },
);
export const getAgencyPharmacyData = createAsyncThunk(
  "pharmacyService/getAgencyPharmacyData",
  async (data, thunkApi) => {
    const url = getAgencyPharmacyUrl(data);
    const respData = await httpGet(url);
    return respData;
  },
);
export const addAgencyPharmacyData = createAsyncThunk(
  "pharmacyService/addAgencyPharmacyData",
  async (data, thunkApi) => {
    const url = addAgencyPharmacyUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);
export const submitCustomTemplateData = createAsyncThunk(
  "pharmacyService/submitCustomTemplateData",
  async ({ filteredModel, companyID, serviceID }, thunkApi) => {
    const url = submitCustomTemplateUrl(companyID, serviceID);
    const respData = await httpPost(url, filteredModel);
    return respData;
  },
);
export const updateClinicalServicePaymentDetailsData = createAsyncThunk(
  "pharmacyService/updateClinicalServicePaymentDetailsData",
  async (clinicalServiceId, thunkApi) => {
    const url = updateClinicalServicePaymentDetailsUrl(clinicalServiceId, true);
    const respData = await httpPost(url);
    return respData;
  },
);
export const getAnalyticsNMSProcessedData = createAsyncThunk(
  "pharmacyService/getAnalyticsNMSProcessedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsNMSProcessedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesEarningsData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesEarningsData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesEarningsUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesUsersData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesUsersData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesUsersUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesStagesData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesStagesData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesStagesUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesCompletedClaimedData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesCompletedClaimedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesCompletedClaimedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesCompletedToBeClaimedData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesCompletedToBeClaimedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId, filterType } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesCompletedToBeClaimedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
      filterType,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAnalyticsServicesDeclinedData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesDeclinedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesDeclinedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getDoseShortCodeListData = createAsyncThunk(
  "pharmacyService/getDoseShortCodeList",
  async (pharmacyId) => {
    const respData = await httpGet(getDoseShortCodeListUrl(pharmacyId));
    return respData;
  },
);
export const updatePharmacyLocationData = createAsyncThunk(
  "pharmacyService/UpdatePharmacyLocation",
  async (data, thunkApi) => {
    const url = UpdatePharmacyLocationUrl();
    const respData = await httpPost(url, data);
    return respData;
  },
);

export const getRoomsData = createAsyncThunk(
  "pharmacyService/getRoomsData",
  async (companyId) => {
    const urlToGet = getRoomsUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getPharmacyContactDetailsData = createAsyncThunk(
  "pharmacyService/getPharmacyContactDetailsData",
  async (pharmacyId) => {
    const urlToGet = getPharmacyContactDetailsUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getUsersByServiceIDData = createAsyncThunk(
  "pharmacyService/getUsersByServiceIDData",
  async (params) => {
    const { serviceID, companyId, pharmacyId } = params;
    const urlToGet = getUsersByServiceIDUrl(serviceID, companyId, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getUsersByServiceCompanyPharmacyIDData = createAsyncThunk(
  "pharmacyService/getUsersByServiceCompanyPharmacyIDData",
  async (params) => {
    const { companyId, serviceId, pharmacyId } = params;
    const urlToGet = getUsersByServiceCompanyPharmacyIDUrl(
      companyId,
      serviceId,
      pharmacyId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getAvailablePharmacistsForServiceData = createAsyncThunk(
  "pharmacyService/getAvailablePharmacistsForServiceData",
  async (params) => {
    const { companyId, serviceId, pharmacyId } = params;
    const urlToGet = getAvailablePharmacistsForServiceUrl(
      companyId,
      serviceId,
      pharmacyId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);
export const getPharmacyAvailableSlotsData = createAsyncThunk(
  "pharmacyService/getPharmacyAvailableSlotsData",
  async (params) => {
    const urlToGet = getPharmacyAvailableSlotsUrl();
    const respData = await httpPost(urlToGet, params);
    return respData;
  },
);
// ActivePharmacistsRequestModel.EventId is an optional GUID. Calendar events
// saved without an EventId are mapped with their Mongo _id (24-hex ObjectId),
// which the API rejects, so only send eventId when it is a GUID.
const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getActivePharmacistsData = createAsyncThunk(
  "pharmacyService/getActivePharmacistsData",
  async (params) => {
    const urlToGet = getActivePharmacistsUrl();
    const { eventId, ...rest } = params || {};
    const body = GUID_REGEX.test(String(eventId ?? "").trim())
      ? { ...rest, eventId: String(eventId).trim() }
      : rest;
    const respData = await httpPost(urlToGet, body);
    return respData;
  },
);

export const getAnalyticsServicesCompletedToBeClaimedHistoricalData = createAsyncThunk(
  "pharmacyService/getAnalyticsServicesCompletedToBeClaimedHistoricalData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId, filterType } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getAnalyticsServicesCompletedToBeClaimedHisotryUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
      filterType,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getHistoryAnalyticsNMSProcessedData = createAsyncThunk(
  "pharmacyService/getHistoryAnalyticsNMSProcessedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getHistoryAnalyticsNMSProcessedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getHistoryAnalyticsServicesCompletedClaimedData = createAsyncThunk(
  "pharmacyService/getHistoryAnalyticsServicesCompletedClaimedData",
  async (params, thunkApi) => {
    const { calendarStartDate, calendarEndDate } =
      thunkApi.getState().pharmacyService || {};
    const { pharmacyId, serviceId } = params || {
      pharmacyId: "00000000-0000-0000-0000-000000000000",
    };

    const urlToGet = getHistoryAnalyticsServicesCompletedClaimedUrl(
      pharmacyId,
      serviceId,
      calendarStartDate,
      calendarEndDate,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getNHSEmergencyServiceData = createAsyncThunk(
  "pharmacyService/getNHSEmergencyServiceData",
  async (pharmacyId, thunkApi) => {
    const urlToGet = getConsultationForNHSEmergencyServiceUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getRoomsForAppointmentData = createAsyncThunk(
  "pharmacyService/getRoomsForAppointmentData",
  async (params) => {
    const urlToGet = getRoomsForAppointmentUrl(params);
    const respData = await httpPost(urlToGet, params);
    return respData;
  },
);

export const getPatientForNHSEmergencyServiceData = createAsyncThunk(
  "pharmacyService/getPatientForNHSEmergencyServiceData",
  async (consultationId, thunkApi) => {
    const respData = await httpGet(
      getPatientForNHSEmergencyServiceUrl(consultationId),
    );
    return respData;
  },
);

export const updateConsultationForPatientMatchData = createAsyncThunk(
  "pharmacyService/updateConsultationForPatientMatchData",
  async ({ data }, thunkApi) => {
    const url = updateConsultationForPatientMatchUrl(data);
    const respData = await httpPost(url, data);
    return respData;
  },
);

export const updateConsultationStageData = createAsyncThunk(
  "pharmacyService/updateConsultationStageData",
  async ({ data }, thunkApi) => {
    const url = updateConsultationStageUrl(data);
    const respData = await httpPost(url, data);
    return respData;
  },
);

export const getReferralDataByConsultationIdData = createAsyncThunk(
  "nhsbar/getReferralDataByConsultationIdData",
  async (consultationId, thunkApi) => {
    const respData = await httpGet(
      getReferralDataByConsultationIdUrl(consultationId),
    );
    return respData;
  },
);

function computeOverdueInfo(item) {
  const notOverdue = {
    ...item,
    isOverdue: false,
    overdueByMs: 0,
    deadlineAt: null,
    deadlineHours: null,
  };
  const disposition = findPharmacyDisposition(item.dispositionCode);
  if (!disposition || disposition.deadlineHours === null) {
    return notOverdue;
  }
  const referralDate = new Date(item.date);
  if (isNaN(referralDate.getTime())) {
    return notOverdue;
  }
  const deadlineMs = disposition.deadlineHours * 60 * 60 * 1000;
  const deadlineAt = new Date(referralDate.getTime() + deadlineMs);
  const overdueByMs = Date.now() - deadlineAt.getTime();
  return {
    ...item,
    isOverdue: overdueByMs > 0,
    overdueByMs: Math.max(0, overdueByMs),
    deadlineAt: deadlineAt.toISOString(),
    deadlineHours: disposition.deadlineHours,
  };
}

function groupNHSEmergencyData(raw) {
  const enriched = raw.map(computeOverdueInfo);
  return [
    {
      title: "New Referrals",
      data: enriched.filter(
        (item) => item.consultationStatus === "New" && !item.isOverdue,
      ),
    },
    {
      title: "Overdue",
      data: enriched.filter(
        (item) => item.consultationStatus !== "Completed" && item.isOverdue,
      ),
    },
    {
      title: "In Progress",
      data: enriched.filter(
        (item) => item.consultationStatus === "Active" && !item.isOverdue,
      ),
    },
  ];
}

export const pharmacyServiceSlice = createSlice({
  name: "pharmacyService",
  initialState,
  reducers: {
    setIsPharmacyServiceLoading: (state, action) => {
      state.isPharmacyServiceLoading = action?.payload || false;
    },
    setIsPharmacyServiceSidebarLoading: (state, action) => {
      state.isPharmacyServiceSiedebarLoading = action?.payload || false;
    },
    setAnyControlChecked(state, action) {
      state.isAnyControlChecked = action.payload;
    },
    // setPatientNameValues: (state, action) => {
    //   state.patientName = action?.payload || false;
    // },
    setPatientNameValues: (state, action) => {
      state.patientName = action.payload || "Tag";
      saveFiltersToStorage(state);
    },

    setShowPaymentModalData: (state, action) => {
      state.showPaymentModal = action?.payload || false;
    },
    setShowConsultationPageData: (state, action) => {
      state.showConsultationPage = action?.payload || false;
    },
    setIsPharmacyServicesAnalyticsLoading: (state, action) => {
      state.isPharmacyAnalyticsLoading = action?.payload || false;
    },
    setIsServiceProgressLoading: (state, action) => {
      state.isServiceProgressLoading = action?.payload || false;
    },
    setIsCalenderShow: (state, action) => {
      state.isCalenderShow = action?.payload || false;
    },
    resetDefaultCalenderAppointmentPharmacyServices: (state) => {
      state.defaultCalenderAppointmentPharmacyServices = [];
    },
    resetServiceUsersByCompany: (state) => {
      state.serviceUsersByCompany = [];
      state.serviceUsersDetailsByCompany = [];
      state.pharmacyAvailableSlots = [];
      state.pharmacyAvailableSlotsDate = "";
    },
    setSidebarVisibleData: (state, action) => {
      state.sidebarVisible = action?.payload || false;
    },
    setCompletedServiceTabValueData: (state, action) => {
      state.completedServiceTabValue = action?.payload || 0;
    },
    setSelectedRow: (state, action) => {
      state.selectedRowData = action?.payload || false;
    },
    setNewClinicComponent: (state, action) => {
      state.newClinicComponent = action?.payload || false;
    },
    setServiceId: (state, action) => {
      state.currentServiceId = action?.payload || "0";
    },
    setCurrentServiceTabValue: (state, action) => {
      state.currentServiceTab = action?.payload || 0;
    },
    setServiceName: (state, action) => {
      state.serviceName = action?.payload || "";
    },
    setServiceIcon: (state, action) => {
      state.serviceIcon = action?.payload || "";
    },
    setServiceBuilderType: (state, action) => {
      state.serviceBuilderType = action?.payload || "0";
    },
    setNHSCount: (state, action) => {
      state.NhsCount = action?.payload || 0;
    },
    setPrivateCount: (state, action) => {
      state.privateCount = action?.payload || 0;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action?.payload || 0;
    },
    setServiceData: (state, action) => {
      state.currentServiceData = action?.payload || [];
    },
    setCurrentEvents: (state, action) => {
      state.currentEvents = action?.payload || [];
    },
    setServiceAnalytics: (state, action) => {
      state.serviceAnalytics = action?.payload || [];
    },
    setCalendarStartDate: (state, action) => {
      state.calendarStartDate = action?.payload;
    },
    setCalendarEndDate: (state, action) => {
      state.calendarEndDate = action?.payload;
    },
    setSmsQueue: (state, action) => {
      state.smsQueue = action?.payload;
    },
    setOrbitQueue: (state, action) => {
      state.orbitQueue = action?.payload;
    },
    setCalendarDates: (state, action) => {
      state.calendarStartDate =
        (Array.isArray(action?.payload) &&
          action?.payload?.length >= 1 &&
          action?.payload[0]) ||
        defaultStartDate.toISOString();
      state.calendarEndDate =
        (Array.isArray(action?.payload) &&
          action?.payload?.length >= 2 &&
          action?.payload[1]) ||
        defaultEndDate.toISOString();
    },
    setNmsProgress: (state, action) => {
      state.isNmsProgress = action?.payload;
    },
    setSentToOrbitData: (state, action) => {
      state.isSentToOrbit = action?.payload;
    },
    setOrbitClinicalServiceIdData: (state, action) => {
      state.orbitClinicalServiceId = action?.payload;
    },
    setPatientContact: (state, action) => {
      state.isPatientContact = action?.payload;
    },
    setContactServiceId: (state, action) => {
      state.contactServiceId = action?.payload;
    },
    setIsFilterModal: (state, action) => {
      state.isFilterModal = action?.payload;
    },
    setFilterPatient: (state, action) => {
      state.filterPatientName = action?.payload || "";
    },
    setFilterNmsValues: (state, action) => {
      state.filterNmsValue = action.payload || "Is";
      saveFiltersToStorage(state);
    },
    setFilterTagsvalues: (state, action) => {
      state.filterTagValues = action.payload || [];
      saveFiltersToStorage(state);
    },
    setFilterTagsvaluesConsented: (state, action) => {
      state.filterTagValuesConsented = action.payload || [];
      saveFiltersToStorage(state);
    },

    setFilterNamevalues: (state, action) => {
      state.filterNameValues = action?.payload || [];
    },
    setPharmacyServices: (state, action) => {
      state.pharmacyServices = action?.payload || [];
    },
    sortPharmacyServicesByStage: (state, action) => {
      const { stageNo, ascending } = action.payload;
      const getItemDate = (cs) => {
        const stage = cs.stages?.find((s) => s.stageType === stageNo);
        return stage?.startDate
          ? new Date(stage.startDate)
          : new Date(cs.clinicalServiceDate);
      };
      const sortClinicalServices = (clinicalServices) =>
        sortClinicalServicesByPatient(clinicalServices, getItemDate, ascending);
      state.pharmacyServices = state.pharmacyServices.map((item) => {
        if (item?.stageType !== stageNo) return item;
        const sortedGroups = item?.sortedServices?.map((service) => ({
          ...service,
          clinicalServices: sortClinicalServices(service?.clinicalServices),
        }));
        const orderedGroups = [...(sortedGroups ?? [])].sort((a, b) => {
          const firstA = a.clinicalServices?.[0];
          const firstB = b.clinicalServices?.[0];
          if (!firstA && !firstB) return 0;
          if (!firstA) return ascending ? 1 : -1;
          if (!firstB) return ascending ? -1 : 1;
          return ascending
            ? getItemDate(firstA) - getItemDate(firstB)
            : getItemDate(firstB) - getItemDate(firstA);
        });
        return { ...item, sortedServices: orderedGroups };
      });
      state.stageSortOrders = {
        ...state.stageSortOrders,
        [stageNo]: ascending,
      };
      saveSortToStorage(state.stageSortOrders);
    },
    setScrollPositionData: (state, action) => {
      state.scrollPosition = action?.payload || 0;
    },
    setOpenData: (state, action) => {
      state.open = action?.payload;
    },
    setAnalyticsTabsValueData: (state, action) => {
      state.analyticsTabValue = action?.payload || 0;
    },
    setServiceProgressData: (state, action) => {
      state.serviceProgress = action?.payload || 0;
    },
    setInterventionQueue: (state, action) => {
      state.interventionQueue = action?.payload;
    },
    setDeclineQueue: (state, action) => {
      state.declineQueue = action?.payload;
    },
    setRescheduleQueue: (state, action) => {
      state.rescheduleQueue = action?.payload;
    },
    setPdsQueue: (state, action) => {
      state.pdsQueue = action?.payload;
    },
    setInterventionStageScrollPositionData: (state, action) => {
      state.interventionStageScrollPosition = action?.payload || 0;
    },
    setEligibilityStageScrollPositionData: (state, action) => {
      state.eligibilityStageScrollPosition = action?.payload || 0;
    },
    setListViewGridScrollPositionData: (state, action) => {
      state.listViewGridScrollPosition = action?.payload || 0;
    },
    setShowRescheduleData: (state, action) => {
      state.showReschedule = action?.payload || 0;
    },
    setCheckedAllEligibleData: (state, action) => {
      state.checkedAllEligible = action?.payload || false;
    },
    setCheckedAllConsentData: (state, action) => {
      state.checkedAllConsent = action?.payload || false;
    },
    setCheckedAllInterventionData: (state, action) => {
      state.checkedAllIntervention = action?.payload || false;
    },
    setCheckedAllFollowUpData: (state, action) => {
      state.checkedAllFollowUp = action?.payload || false;
    },
    setProcessedPDSServicesData: (state, action) => {
      state.processedPDSServices = action?.payload || [];
    },
    setDrugFilterValueData: (state, action) => {
      state.drugFilterValue = action?.payload || "";
    },
    setSendSMSAutomationData: (state, action) => {
      state.sendSMSAutomation = action?.payload || false;
    },
    setSendOrbitAutomationData: (state, action) => {
      state.sendOrbitAutomation = action?.payload || false;
    },
    setTransformedPharmacyData: (state, action) => {
      state.pharmacyServices = action?.payload || [];
    },
    setSendInterventionAutomationData: (state, action) => {
      state.sendInterventionAutomation = action?.payload || false;
    },
    setSendConsentOnlyData: (state, action) => {
      state.sendConsentOnly = action?.payload || false;
    },
    setSendToInterventionOptionsData: (state, action) => {
      state.sendToInterventionOptions = action?.payload || null;
    },
    setFilteredPharmacyServicesData: (state, action) => {
      state.filterPharmacyService = action?.payload || false;
    },
    setOriginalPharmacyServicesData: (state, action) => {
      state.originalPharmacyServices = action?.payload || false;
    },
    setIsAutomationMenuOpenData: (state, action) => {
      state.isAutomationMenuOpen = action?.payload || false;
    },
    setSelectedClinicalServiceIdData: (state, action) => {
      state.selectedClinicalServiceId = action?.payload || "";
    },
    setOrbitSendModal: (state, action) => {
      state.isOrbitSendModal = action?.payload;
    },
    setDeclineModal: (state, action) => {
      state.isDeclineModal = action?.payload;
    },
    setTemplateControlValue: (state, action) => {
      const {
        templateIndex,
        sectionIndex,
        controlIndex,
        value,
      } = action.payload;
      state.customTemplateModel.templates[templateIndex].templateSections[
        sectionIndex
      ].userControls[controlIndex].value = value;
    },
    setOrbitModalQueueData: (state, action) => {
      state.orbitModalQueue = action?.payload || [];
    },
    setDeclineModalQueueData: (state, action) => {
      state.declineModalQueue = action?.payload || [];
    },
    setServiceTypeData: (state, action) => {
      state.serviceType = action?.payload || false;
    },
    setCurrentServieIdData: (state, action) => {
      state.currentPharmacyServiceId = action?.payload || "";
    },
    addDayToServiceAvailability: (state, action) => {
      const { pharmacyId, serviceId, day } = action.payload;

      const updatedState = JSON.parse(JSON.stringify(state));

      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy?.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service) {
          if (!service.availability) {
            service.availability = [];
          }

          const dayExists = service.availability.some(
            (existingDay) => existingDay.dayOfWeek === day.dayOfWeek,
          );

          if (!dayExists) {
            service.availability.push(day);
          }
        }
      }

      // Update the state with the modified copy
      state.allPharmacies = updatedState.allPharmacies;
    },

    removeDayFromServiceAvailability: (state, action) => {
      const { pharmacyId, serviceId, dayOfWeek } = action.payload;

      // Create a deep copy of the state to avoid mutation
      const updatedState = JSON.parse(JSON.stringify(state));

      // Find the pharmacy based on pharmacyId
      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy?.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        // Find the service based on serviceId
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service && service.availability) {
          // Filter out the day that needs to be removed
          service.availability = service.availability.filter(
            (day) => day.dayOfWeek !== dayOfWeek,
          );
        }
      }

      // Update the state with the modified copy
      state.allPharmacies = updatedState.allPharmacies;
    },
    updateSlotTimes: (state, action) => {
      const { pharmacyId, serviceId, dayOfWeek, updatedSlots } = action.payload;

      // Create a deep copy of the state to avoid mutation
      const updatedState = JSON.parse(JSON.stringify(state));

      // Find the pharmacy based on pharmacyId
      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy?.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        // Find the service based on serviceId
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service && service.availability) {
          // Find the day based on dayOfWeek
          const day = service.availability.find(
            (day) => day.dayOfWeek === dayOfWeek,
          );

          if (day) {
            // Update the slots for the matching day
            day.slots = updatedSlots;
          }
        }
      }

      // Update the state with the modified copy
      state.allPharmacies = updatedState.allPharmacies;
    },
    addNewSlot: (state, action) => {
      const { pharmacyId, serviceId, dayOfWeek } = action.payload;

      const updatedState = JSON.parse(JSON.stringify(state));

      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service && service.availability) {
          const day = service.availability.find(
            (day) => day.dayOfWeek === dayOfWeek,
          );

          if (day) {
            // Add a new slot with default values
            day.slots.push({
              startTime: "",
              endTime: "",
            });
          }
        }
      }

      state.allPharmacies = updatedState.allPharmacies;
    },
    removeSlot: (state, action) => {
      const { pharmacyId, serviceId, dayOfWeek, slotIndex } = action.payload;

      const updatedState = JSON.parse(JSON.stringify(state));

      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service && service.availability) {
          const day = service.availability.find(
            (day) => day.dayOfWeek === dayOfWeek,
          );

          if (day) {
            // Remove the slot by index
            day.slots.splice(slotIndex, 1);
          }
        }
      }

      state.allPharmacies = updatedState.allPharmacies;
    },
    duplicateSlot: (state, action) => {
      const { pharmacyId, serviceId, dayOfWeek, slotIndex } = action.payload;

      const updatedState = JSON.parse(JSON.stringify(state));

      const pharmacy = updatedState.allPharmacies.find(
        (pharmacy) => pharmacy.pharmacyID === pharmacyId,
      );

      if (pharmacy) {
        const service = pharmacy.pharmacyServices.find(
          (service) => service._id === serviceId,
        );

        if (service && service.availability) {
          const day = service.availability.find(
            (day) => day.dayOfWeek === dayOfWeek,
          );

          if (day) {
            // Copy the slot and add it to the slots array
            const newSlot = { ...day.slots[slotIndex] };
            day.slots.push(newSlot);
          }
        }
      }

      state.allPharmacies = updatedState.allPharmacies;
    },
    setCurrentCompletedServiceValue: (state, action) => {
      state.currentCompletedServiceValue = action?.payload || "1";
    },
    setShowNHSEmergencyKanbanData: (state, action) => {
      state.showNHSEmergencyKanban = action?.payload || false;
    },
    setNHSEmergencyServiceData: (state, action) => {
      state.NHSEmergencyServiceData = action?.payload || [];
    },
    recomputeNHSEmergencyOverdue: (state) => {
      if (state.rawNHSEmergencyData.length > 0) {
        state.NHSEmergencyServiceData = groupNHSEmergencyData(
          state.rawNHSEmergencyData,
        );
      }
    },
    setAppointmentRoomsData: (state, action) => {
      state.appointmentRooms = action?.payload || [];
    },
    setMatchDialogModalData: (state, action) => {
      state.matchDialogOpen = action?.payload || false;
    },
    setMatchPatientData: (state, action) => {
      state.matchPatient = action?.payload || "";
    },
    setIsYotiSuccess: (state, action) => {
      state.isYotiSuccess = action?.payload || false;
    },
    setShowNHS111ReferralTabData: (state, action) => {
      state.showNHS111ReferralTab = action?.payload || false;
    },
    resetPharmacyServiceState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPharmacyServiceData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getPharmacyServiceData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        const appDataFromResponse = action?.payload;

        state.pharmacyServices =
          appDataFromResponse?.clinicalServicesSortedModels || [];

        state.filterPharmacyService =
          appDataFromResponse?.clinicalServicesSortedModels || [];

        state.originalPharmacyServices =
          appDataFromResponse?.clinicalServicesSortedModels || [];

        state.pharmacyServicesSms =
          appDataFromResponse?.clinicalServiceSMSDates || [];
        let patientTag = [];
        let patientTagColor = [];
        appDataFromResponse?.clinicalServicesSortedModels?.map((appData) => {
          appData?.sortedServices?.map((sortedServices) => {
            sortedServices?.clinicalServices?.map((clinicalService) => {
              clinicalService?.patientTags?.map((pTags) => {
                const tagToAdd = pTags?.tagName;
                if (!patientTag.includes(tagToAdd)) {
                  patientTag = [...patientTag, tagToAdd];
                }
                const isPresent = patientTagColor.some(
                  (item) => item.tagName === pTags.tagName,
                );
                if (!isPresent) {
                  patientTagColor = [...patientTagColor, pTags];
                }
              });
            });
          });
        });

        let gridRowData = appDataFromResponse?.clinicalServicesSortedModels;

        function calculateAge(dob) {
          const dobDate = new Date(dob);

          if (isNaN(dobDate.getTime()) || dobDate.getFullYear() === 1) {
            return "-";
          }
          const currentDate = new Date();
          const diffMs = currentDate - dobDate;
          const age = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365.25));

          return age;
        }

        const transformData = (data, orbitQueue) => {
          let result = [];
          let uniqueSet = new Set(); // To store unique combinations

          data?.forEach((stage) => {
            stage?.sortedServices?.forEach((service) => {
              service?.clinicalServices?.forEach((clinicalService) => {
                if (clinicalService?.serviceMedications?.length > 0) {
                  clinicalService?.serviceMedications?.forEach((medication) => {
                    const key = `${clinicalService?.patientName}|${
                      medication?.drugName
                    }|${getDueName(service?.dueType)}`;
                    if (!uniqueSet.has(key)) {
                      uniqueSet.add(key);
                      let rowIndex = uuidv4();
                      let row = {
                        rowIndex: rowIndex,
                        stageName: stage?.stageName,
                        patientName: clinicalService?.patientName,
                        patientNameFilter: clinicalService?.patientName,
                        age: calculateAge(clinicalService?.dob),
                        drugName: medication?.drugName,
                        dosage: medication?.dosage,
                        due: getDueName(service?.dueType),
                        patientTags: clinicalService?.patientTags,
                        handedoutDate: moment(
                          clinicalService?.clinicalServiceDate,
                        ).format("DD/MM/YYYY"),
                        consented: clinicalService?.isConsent ? "Yes" : "No",
                        hasTelephoneNumber: clinicalService?.telephoneNumber
                          ? "Yes"
                          : "No",
                        clinicalServiceId: clinicalService?._id,
                        stageType: stage?.stageType,
                        service: clinicalService,
                        sendToOrbit: clinicalService?.sendToOrbit,
                      };
                      result.push(row);
                    }
                  });
                } else {
                  // Keep your original else block unchanged
                  let rowIndex = uuidv4();
                  let row = {
                    rowIndex: rowIndex,
                    stageName: stage?.stageName,
                    patientName: clinicalService?.patientName,
                    patientNameFilter: clinicalService?.patientName,
                    age: calculateAge(clinicalService?.dob),
                    drugName: "",
                    dosage: "",
                    due: getDueName(service?.dueType),
                    patientTags: clinicalService?.patientTags,
                    handedoutDate: moment(
                      clinicalService?.clinicalServiceDate,
                    ).format("DD/MM/YYYY"),
                    consented: clinicalService?.isConsent ? "Yes" : "No",
                    hasTelephoneNumber: clinicalService?.telephoneNumber
                      ? "Yes"
                      : "No",
                    clinicalServiceId: clinicalService?._id,
                    stageType: stage?.stageType,
                    service: clinicalService,
                    sendToOrbit: clinicalService?.sendToOrbit,
                  };
                  result.push(row);
                }
              });
            });
          });

          return result;
        };
        state.transformedPharmacyData = transformData(gridRowData);

        if (patientTag?.length > 0) {
          state.patientTags = patientTag;
          state.patientTagsColor = patientTagColor;
        }
        if (state.filterTagValues?.length > 0) {
          if (state.filterNmsValue === "Is") {
            const updatedPharmacyService = state.filterPharmacyService?.map(
              (stage) => ({
                ...stage,
                sortedServices: stage?.sortedServices?.map((service) => ({
                  ...service,
                  clinicalServices: service?.clinicalServices?.filter(
                    (patient) =>
                      patient?.patientTags?.some((tag) =>
                        state.filterTagValues.includes(tag?.tagName),
                      ),
                  ),
                })),
              }),
            );
            state.pharmacyServices = updatedPharmacyService;
          } else {
            const updatedPharmacyService = state.filterPharmacyService?.map(
              (stage) => ({
                ...stage,
                sortedServices: stage?.sortedServices?.map((service) => ({
                  ...service,
                  clinicalServices: service?.clinicalServices?.filter(
                    (patient) =>
                      !patient?.patientTags?.some((tag) =>
                        state.filterTagValues.includes(tag?.tagName),
                      ),
                  ),
                })),
              }),
            );
            state.pharmacyServices = updatedPharmacyService;
          }
        }
        const orders = state.stageSortOrders;
        if (Object.keys(orders).length > 0) {
          const applySort = (services, stageNo) => {
            const ascending = orders[stageNo];
            const getItemDate = (cs) => {
              const stage = cs.stages?.find((s) => s.stageType === stageNo);
              return stage?.startDate
                ? new Date(stage.startDate)
                : new Date(cs.clinicalServiceDate);
            };
            return services.map((item) => {
              if (item?.stageType !== stageNo) return item;
              const sortedGroups = item?.sortedServices?.map((service) => ({
                ...service,
                clinicalServices: sortClinicalServicesByPatient(
                  service?.clinicalServices,
                  getItemDate,
                  ascending,
                ),
              }));
              const orderedGroups = [...(sortedGroups ?? [])].sort((a, b) => {
                const firstA = a.clinicalServices?.[0];
                const firstB = b.clinicalServices?.[0];
                if (!firstA && !firstB) return 0;
                if (!firstA) return ascending ? 1 : -1;
                if (!firstB) return ascending ? -1 : 1;
                return ascending
                  ? getItemDate(firstA) - getItemDate(firstB)
                  : getItemDate(firstB) - getItemDate(firstA);
              });
              return { ...item, sortedServices: orderedGroups };
            });
          };
          let sorted = state.pharmacyServices;
          for (const stageNo of Object.keys(orders)) {
            sorted = applySort(sorted, Number(stageNo));
          }
          state.pharmacyServices = sorted;
        }
      })
      .addCase(getPharmacyServiceData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyServiceLoading = false;
        state.pharmacyServices = [];
        state.filterPharmacyService = [];
        state.pharmacyServicesSms = [];
      })
      .addCase(getPharmacyClinicalServiceData.pending, (state) => {
        state.isPharmacyServiceSiedebarLoading = true;
      })
      .addCase(getPharmacyClinicalServiceData.fulfilled, (state, action) => {
        const appDataFromResponse = action?.payload;
        state.pharmacyClincalServices = appDataFromResponse || [];
      })
      .addCase(getPharmacyClinicalServiceData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.pharmacyClincalServices = [];
      })
      .addCase(getPharmacyClinicalServiceCountData.pending, (state) => {
        state.isPharmacyServiceSiedebarLoading = true;
      })
      .addCase(
        getPharmacyClinicalServiceCountData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceSiedebarLoading = false;
          const appDataFromResponse = action?.payload;
          state.pharmacyClincalServicesCount = appDataFromResponse || [];
        },
      )
      .addCase(
        getPharmacyClinicalServiceCountData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isPharmacyServiceSiedebarLoading = false;
          state.pharmacyClincalServicesCount = [];
        },
      )
      .addCase(getClinicalServiceBuilderByServiceIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(
        getClinicalServiceBuilderByServiceIdData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          const appDataFromResponse = action?.payload;
          state.clinicalServiceBuilderModel = appDataFromResponse || [];
        },
      )
      .addCase(
        getClinicalServiceBuilderByServiceIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isPharmacyServiceLoading = false;
          state.clinicalServiceBuilderModel = [];
        },
      )
      .addCase(
        getClinicalServiceByCompanyAndServiceIdData.pending,
        (state) => {},
      )
      .addCase(
        getClinicalServiceByCompanyAndServiceIdData.fulfilled,
        (state, action) => {
          const appDataFromResponse = action?.payload;
          state.ClinicalServiceBuilder = appDataFromResponse || [];
        },
      )
      .addCase(
        getClinicalServiceByCompanyAndServiceIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.ClinicalServiceBuilder = [];
        },
      )
      .addCase(getAnalyticsByServicePharmacyDateData.pending, (state) => {
        state.isPharmacyAnalyticsLoading = true;
      })
      .addCase(
        getAnalyticsByServicePharmacyDateData.fulfilled,
        (state, action) => {
          state.isPharmacyAnalyticsLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceAnalytics = appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getAnalyticsByServicePharmacyDateData.rejected,
        (state, action) => {
          state.isPharmacyAnalyticsLoading = false;
          state.serviceAnalytics = [];
        },
      )
      .addCase(getEventsData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getEventsData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.currentEvents = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getEventsData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.currentEvents = [];
      })
      .addCase(getPharmaciesByCompanyIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getPharmaciesByCompanyIdData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.allPharmacies = action?.payload || [];
      })
      .addCase(getPharmaciesByCompanyIdData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.allPharmacies = [];
      })
      .addCase(addpharmacyServiceData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(addpharmacyServiceData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.currentPharmacyService = action?.payload || null;
      })
      .addCase(addpharmacyServiceData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyServiceLoading = false;
        state.currentPharmacyService = null;
      })
      .addCase(updateEventData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(updateEventData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.updatedEvent = action?.payload || null;
      })
      .addCase(updateEventData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyServiceLoading = false;
        state.updatedEvent = null;
      })
      .addCase(
        updateCalendarEventByClinicalServiceIdData.pending,
        (state) => {},
      )
      .addCase(
        updateCalendarEventByClinicalServiceIdData.fulfilled,
        (state, action) => {},
      )
      .addCase(
        updateCalendarEventByClinicalServiceIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
        },
      )
      .addCase(updateRecheduleByClinicalServiceIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(
        updateRecheduleByClinicalServiceIdData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
        },
      )
      .addCase(
        updateRecheduleByClinicalServiceIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isPharmacyServiceLoading = false;
        },
      )
      .addCase(sendSMSData.pending, (state) => {})
      .addCase(sendSMSData.fulfilled, (state, action) => {})
      .addCase(sendSMSData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
      })
      .addCase(sendToOrbitData.pending, (state) => {})
      .addCase(sendToOrbitData.fulfilled, (state, action) => {})
      .addCase(sendToOrbitData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
      })
      .addCase(sendToAgencyOrbitData.pending, (state) => {})
      .addCase(sendToAgencyOrbitData.fulfilled, (state, action) => {})
      .addCase(sendToAgencyOrbitData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
      })
      .addCase(updateClaimStatusData.pending, (state) => {
        state.isSentClaim = true;
      })
      .addCase(updateClaimStatusData.fulfilled, (state, action) => {
        state.isSentClaim = false;
      })
      .addCase(updateClaimStatusData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isSentClaim = false;
      })
      .addCase(getSMSData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getSMSData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.smsUrls = action?.payload || [];
      })
      .addCase(getSMSData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.smsUrls = [];
      })
      .addCase(getIdentityVerificationData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getIdentityVerificationData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.yotiSessionDetail = action?.payload || null;
      })
      .addCase(getIdentityVerificationData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.yotiSessionDetail = null;
      })
      .addCase(getIdentityVerificationDataBySessionIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.isYotiSuccess = false;
      })
      .addCase(
        getIdentityVerificationDataBySessionIdData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse.length === 0) {
            state.isYotiSuccess = true;
          }
        },
      )
      .addCase(
        getIdentityVerificationDataBySessionIdData.rejected,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          state.isYotiSuccess = false;
        },
      )
      .addCase(checkYotiDataBySessionIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.isYotiSuccess = false;
      })
      .addCase(checkYotiDataBySessionIdData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse.length === 0) {
          state.isYotiSuccess = true;
        }
      })
      .addCase(checkYotiDataBySessionIdData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.isYotiSuccess = false;
      })
      .addCase(getOrbitSessionDetailData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.orbitSessionDetail = null;
      })
      .addCase(getOrbitSessionDetailData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.orbitSessionDetail = action?.payload || null;
      })
      .addCase(getOrbitSessionDetailData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.orbitSessionDetail = null;
      })
      .addCase(changeSentToOrbitData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(changeSentToOrbitData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(changeSentToOrbitData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(saveOrbitSessionDetailData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.orbitSession = null;
      })
      .addCase(saveOrbitSessionDetailData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.orbitSession = action?.payload || null;
      })
      .addCase(saveOrbitSessionDetailData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.orbitSession = null;
      })
      .addCase(getUserLogData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.userLog = null;
      })
      .addCase(getUserLogData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        const appDataFromResponse = action?.payload;
        state.userLog = appDataFromResponse || null;
        state.isNmsProgress = appDataFromResponse?.isActive || false;
        state.userName = appDataFromResponse?.userName || "";
      })
      .addCase(getUserLogData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.userLog = null;
      })
      .addCase(savePatientContactData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.savePatientContact = null;
      })
      .addCase(savePatientContactData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.savePatientContact = action?.payload || null;

        const updatedPhone =
          action?.payload?.telephoneNumber ?? action?.meta?.arg?.telephone;
        const serviceId = action?.meta?.arg?.serviceId;
        if (serviceId && updatedPhone) {
          const updateTelephone = (services) => {
            services?.forEach((stage) => {
              stage?.sortedServices?.forEach((sorted) => {
                sorted?.clinicalServices?.forEach((svc) => {
                  if (svc._id === serviceId) {
                    svc.telephoneNumber = updatedPhone;
                  }
                });
              });
            });
          };
          updateTelephone(state.pharmacyServices);
          updateTelephone(state.filterPharmacyService);
          updateTelephone(state.originalPharmacyServices);
        }
      })
      .addCase(savePatientContactData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.savePatientContact = null;
      })
      .addCase(getServiceProgressData.pending, (state) => {
        state.isServiceProgressLoading = true;
        state.serviceProgress = 0;
      })
      .addCase(getServiceProgressData.fulfilled, (state, action) => {
        state.isServiceProgressLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceProgress = appDataFromResponse || 0;
          } catch (error) {}
        }
      })
      .addCase(getServiceProgressData.rejected, (state, action) => {
        state.isServiceProgressLoading = false;
        state.serviceProgress = 0;
      })
      .addCase(getPharmacyActionsData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
        state.pharmacyActions = null;
      })
      .addCase(getPharmacyActionsData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.pharmacyActions = appDataFromResponse || null;
            state.sendSMSAutomation = appDataFromResponse?.sendSMS;
            state.sendOrbitAutomation = appDataFromResponse?.sendToOrbit;

            state.sendInterventionAutomation =
              appDataFromResponse?.sendToIntervention;
            state.sendConsentOnly = appDataFromResponse?.consentOnly;
            if (appDataFromResponse?.sendToIntervention === true) {
              state.sendToInterventionOptions =
                state.SendToInterventionDropdownOptions[1];
            } else {
              state.sendToInterventionOptions =
                state.SendToInterventionDropdownOptions[0];
            }
          } catch (error) {}
        }
      })
      .addCase(getPharmacyActionsData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.pharmacyActions = null;
      })
      .addCase(changePharmacyActionsData.pending, (state) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(changePharmacyActionsData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(changePharmacyActionsData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(getPharmacyDefaultCheckListData.pending, (state) => {})
      .addCase(getPharmacyDefaultCheckListData.fulfilled, (state, action) => {})
      .addCase(getPharmacyDefaultCheckListData.rejected, (state, action) => {})
      .addCase(getServiceQRCheckListData.pending, (state) => {})
      .addCase(getServiceQRCheckListData.fulfilled, (state, action) => {})
      .addCase(getServiceQRCheckListData.rejected, (state, action) => {})
      .addCase(duplicateServiceData.pending, (state) => {})
      .addCase(duplicateServiceData.fulfilled, (state, action) => {
        state.serviceDuplicated = action?.payload;
      })
      .addCase(duplicateServiceData.rejected, (state) => {
        state.serviceDuplicated = false;
      })
      .addCase(moveDeclineServiceToClinicalServiceData.pending, (state) => {
        state.isPharmacyServiceLoading = false;
        state.isRevertBackToClinicalService = false;
      })
      .addCase(
        moveDeclineServiceToClinicalServiceData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.isRevertBackToClinicalService = true;
            } catch (error) {}
          }
        },
      )
      .addCase(
        moveDeclineServiceToClinicalServiceData.rejected,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          state.isRevertBackToClinicalService = false;
        },
      )
      .addCase(moveInterventionData.pending, (state) => {})
      .addCase(moveInterventionData.fulfilled, (state, action) => {})
      .addCase(moveInterventionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
      })
      .addCase(declineServicesData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(declineServicesData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(declineServicesData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
        logErrorWithPayload(action?.error);
      })
      .addCase(rescheduleServicesData.pending, (state) => {})
      .addCase(rescheduleServicesData.fulfilled, (state, action) => {})
      .addCase(rescheduleServicesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
      })
      .addCase(getServiceTitanPatientPdsData.rejected, (state, action) => {})
      .addCase(getServiceTitanPatientPdsData.pending, (state) => {})
      .addCase(getServiceTitanPatientPdsData.fulfilled, (state, action) => {})

      .addCase(getDefaultCompanyServiceUrlData.pending, (state, action) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getDefaultCompanyServiceUrlData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.defaultPharmacyServices = action?.payload;
      })
      .addCase(getDefaultCompanyServiceUrlData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(
        getDefaultServicesByCompanyIdAndPharmacyIdData.pending,
        (state, action) => {
          state.isPharmacyServiceLoading = true;
        },
      )
      .addCase(
        getDefaultServicesByCompanyIdAndPharmacyIdData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          state.defaultCalenderAppointmentPharmacyServices = action?.payload;
        },
      )
      .addCase(
        getDefaultServicesByCompanyIdAndPharmacyIdData.rejected,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
        },
      )
      .addCase(getClinicalServiceBuilderByIdData.pending, (state, action) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getClinicalServiceBuilderByIdData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        state.serviceDetails = action?.payload;
      })
      .addCase(getClinicalServiceBuilderByIdData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(getServiceTemplatesData.pending, (state, action) => {})
      .addCase(getServiceTemplatesData.fulfilled, (state, action) => {
        state.serviceDetailTemplates = action?.payload;
        state.customTemplateModel = {
          templates: action?.payload.map((template) => ({
            ...template,
            templateSections: template.templateSections.map((section) => ({
              ...section,
              userControls: section.userControls.map((control) => ({
                ...control,
                value: true,
              })),
            })),
          })),
        };
      })
      .addCase(getServiceTemplatesData.rejected, (state, action) => {})
      .addCase(submitCustomTemplateData.pending, (state, action) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(submitCustomTemplateData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(submitCustomTemplateData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      .addCase(addAgencyPharmacyData.pending, (state, action) => {
        state.isAgencyRatesLoading = true;
      })
      .addCase(addAgencyPharmacyData.fulfilled, (state, action) => {
        state.isAgencyRatesLoading = false;
      })
      .addCase(addAgencyPharmacyData.rejected, (state, action) => {
        state.isAgencyRatesLoading = false;
      })
      .addCase(getClinicalServiceBuilderByCompanyIdData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(
        getClinicalServiceBuilderByCompanyIdData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          state.companyServices = action?.payload || [];
        },
      )
      .addCase(
        getClinicalServiceBuilderByCompanyIdData.rejected,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
          state.companyServices = [];
        },
      )
      .addCase(updateClinicalServicePaymentDetailsData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(
        updateClinicalServicePaymentDetailsData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
        },
      )
      .addCase(
        updateClinicalServicePaymentDetailsData.rejected,
        (state, action) => {
          state.isPharmacyServiceLoading = false;
        },
      )
      .addCase(getAgencyRatesData.pending, (state) => {
        state.isAgencyRatesLoading = true;
      })
      .addCase(getAgencyRatesData.fulfilled, (state, action) => {
        state.isAgencyRatesLoading = false;
        state.agencyRates = action?.payload || [];
      })
      .addCase(getAgencyRatesData.rejected, (state, action) => {
        state.isAgencyRatesLoading = false;
        state.agencyRates = [];
      })
      .addCase(getAgencyPharmacyData.pending, (state) => {
        state.isPharmacyServiceLoading = true;
      })
      .addCase(getAgencyPharmacyData.fulfilled, (state, action) => {
        state.isPharmacyServiceLoading = false;
        if (action?.payload !== null) {
          state.IsPharmacyAgency = true;
          state.agencyPharmacy = action?.payload;
        } else {
          state.IsPharmacyAgency = false;
        }
      })
      .addCase(getAgencyPharmacyData.rejected, (state, action) => {
        state.isPharmacyServiceLoading = false;
      })
      // analytics optimization changes
      // NMS processed data
      .addCase(getAnalyticsNMSProcessedData.pending, (state) => {
        state.isPharmacyServiceProcessedLoading = true;
      })
      .addCase(getAnalyticsNMSProcessedData.fulfilled, (state, action) => {
        state.isPharmacyServiceProcessedLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceAnalyticsProcessed = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getAnalyticsNMSProcessedData.rejected, (state, action) => {
        state.isPharmacyServiceProcessedLoading = false;
        state.serviceAnalyticsProcessed = [];
      })
      .addCase(getHistoryAnalyticsNMSProcessedData.pending, (state) => {
        state.isPharmacyServiceProcessedLoading = true;
      })
      .addCase(
        getHistoryAnalyticsNMSProcessedData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceProcessedLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceHistoricalAnalyticsProcessed =
                appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getHistoryAnalyticsNMSProcessedData.rejected,
        (state, action) => {
          state.isPharmacyServiceProcessedLoading = false;
          state.serviceHistoricalAnalyticsProcessed = [];
        },
      )
      // analytics earning data
      .addCase(getAnalyticsServicesEarningsData.pending, (state) => {
        state.isPharmacyServiceEarningLoading = true;
      })
      .addCase(getAnalyticsServicesEarningsData.fulfilled, (state, action) => {
        state.isPharmacyServiceEarningLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceAnalyticsEarning = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getAnalyticsServicesEarningsData.rejected, (state, action) => {
        state.isPharmacyServiceEarningLoading = false;
        state.serviceAnalyticsEarning = [];
      })
      // analytics service users data
      .addCase(getAnalyticsServicesUsersData.pending, (state) => {
        state.isPharmacyServiceUsersLoading = true;
      })
      .addCase(getAnalyticsServicesUsersData.fulfilled, (state, action) => {
        state.isPharmacyServiceUsersLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceAnalyticsUsers = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getAnalyticsServicesUsersData.rejected, (state, action) => {
        state.isPharmacyServiceUsersLoading = false;
        state.serviceAnalyticsUsers = [];
      })
      // analytics service stages data
      .addCase(getAnalyticsServicesStagesData.pending, (state) => {
        state.isPharmacyServiceStagesLoading = true;
      })
      .addCase(getAnalyticsServicesStagesData.fulfilled, (state, action) => {
        state.isPharmacyServiceStagesLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceAnalyticsStages = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getAnalyticsServicesStagesData.rejected, (state, action) => {
        state.isPharmacyServiceStagesLoading = false;
        state.serviceAnalyticsStages = [];
      })
      // analytics service completed claimed data
      .addCase(getAnalyticsServicesCompletedClaimedData.pending, (state) => {
        state.isPharmacyServiceCompletedClaimedLoading = true;
      })
      .addCase(
        getAnalyticsServicesCompletedClaimedData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceCompletedClaimedLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceAnalyticsCompletedClaimed =
                appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getAnalyticsServicesCompletedClaimedData.rejected,
        (state, action) => {
          state.isPharmacyServiceCompletedClaimedLoading = false;
          state.serviceAnalyticsCompletedClaimed = [];
        },
      )
      .addCase(
        getHistoryAnalyticsServicesCompletedClaimedData.pending,
        (state) => {
          state.isPharmacyServiceCompletedClaimedLoading = true;
        },
      )
      .addCase(
        getHistoryAnalyticsServicesCompletedClaimedData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceCompletedClaimedLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceHistoryAnalyticsCompletedClaimed =
                appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getHistoryAnalyticsServicesCompletedClaimedData.rejected,
        (state, action) => {
          state.isPharmacyServiceCompletedClaimedLoading = false;
          state.serviceHistoryAnalyticsCompletedClaimed = [];
        },
      )
      // analytics service completed tobe claimed data
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedData.pending,
        (state) => {
          state.isPharmacyServiceCompletedTobeClaimedLoading = true;
        },
      )
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceCompletedTobeClaimedLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceAnalyticsCompletedTobeClaimed =
                appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedData.rejected,
        (state, action) => {
          state.isPharmacyServiceCompletedTobeClaimedLoading = false;
          state.serviceAnalyticsCompletedTobeClaimed = [];
        },
      )
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedHistoricalData.pending,
        (state) => {
          state.isPharmacyServiceCompletedTobeClaimedHistoryLoading = true;
        },
      )
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedHistoricalData.fulfilled,
        (state, action) => {
          state.isPharmacyServiceCompletedTobeClaimedHistoryLoading = false;
          const appDataFromResponse = action?.payload;
          if (appDataFromResponse) {
            try {
              state.serviceAnalyticsCompletedTobeClaimedHistorical =
                appDataFromResponse || [];
            } catch (error) {}
          }
        },
      )
      .addCase(
        getAnalyticsServicesCompletedToBeClaimedHistoricalData.rejected,
        (state, action) => {
          state.isPharmacyServiceCompletedTobeClaimedHistoryLoading = false;
          state.serviceAnalyticsCompletedTobeClaimedHistorical = [];
        },
      )

      // analytics service declined data
      .addCase(getAnalyticsServicesDeclinedData.pending, (state) => {
        state.isPharmacyServiceCompletedDeclinedLoading = true;
      })
      .addCase(getAnalyticsServicesDeclinedData.fulfilled, (state, action) => {
        state.isPharmacyServiceCompletedDeclinedLoading = false;
        const appDataFromResponse = action?.payload;
        if (appDataFromResponse) {
          try {
            state.serviceAnalyticsCompletedDeclined = appDataFromResponse || [];
          } catch (error) {}
        }
      })
      .addCase(getAnalyticsServicesDeclinedData.rejected, (state, action) => {
        state.isPharmacyServiceCompletedDeclinedLoading = false;
        state.serviceAnalyticsCompletedDeclined = [];
      })
      .addCase(getDoseShortCodeListData.fulfilled, (state, action) => {
        state.isDoseShortCodeListLoading = false;
        state.doseShortCodeList = action?.payload || [];
      })

      .addCase(updatePharmacyLocationData.pending, (state) => {
        state.isUpdatePharmacyLocationLoading = true;
      })
      .addCase(updatePharmacyLocationData.fulfilled, (state, action) => {
        state.isUpdatePharmacyLocationLoading = false;
        state.updatedPharmacy = action?.payload || null;
      })
      .addCase(updatePharmacyLocationData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUpdatePharmacyLocationLoading = false;
        state.updatedPharmacy = null;
      })
      .addCase(getRoomsData.fulfilled, (state, action) => {
        state.isRoomLoading = false;
        state.roomsData = action?.payload || [];
      })
      .addCase(getRoomsData.pending, (state) => {
        state.isRoomLoading = true;
      })
      .addCase(getRoomsData.rejected, (state, action) => {
        state.isRoomLoading = false;
        state.roomsData = [];
      })

      .addCase(getUsersByServiceIDData.pending, (state) => {
        state.isUpdatePharmacyLocationLoading = true;
        state.isServiceUsersByCompanyLoading = true;
      })
      .addCase(getUsersByServiceIDData.fulfilled, (state, action) => {
        state.isUpdatePharmacyLocationLoading = false;
        state.isServiceUsersByCompanyLoading = false;
        const payloadData = action?.payload?.response || action?.payload;
        const users = Array.isArray(payloadData)
          ? payloadData
          : payloadData?.user || [];
        const rooms = payloadData?.room || [];
        const openingHours = payloadData?.openingHours || [];
        state.serviceUsersByCompany = users;
        state.serviceUsersDetailsByCompany = users;
        state.serviceRoomsByCompany = rooms;
        state.pharmacyOpeningHours = openingHours;
      })
      .addCase(getUsersByServiceIDData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUpdatePharmacyLocationLoading = false;
        state.isServiceUsersByCompanyLoading = false;
        state.serviceUsersByCompany = [];
        state.serviceUsersDetailsByCompany = [];
        state.serviceRoomsByCompany = [];
        state.pharmacyOpeningHours = [];
      })
      .addCase(getUsersByServiceCompanyPharmacyIDData.pending, (state) => {
        state.isUpdatePharmacyLocationLoading = true;
        state.isServiceUsersByCompanyLoading = true;
      })
      .addCase(
        getUsersByServiceCompanyPharmacyIDData.fulfilled,
        (state, action) => {
          state.isUpdatePharmacyLocationLoading = false;
          state.isServiceUsersByCompanyLoading = false;
          const payloadData = action?.payload?.response || action?.payload;
          const users = Array.isArray(payloadData)
            ? payloadData
            : payloadData?.user || [];
          const rooms = payloadData?.room || [];
          const openingHours = payloadData?.openingHours || [];
          state.serviceUsersByCompany = users;
          state.serviceUsersDetailsByCompany = users;
          state.serviceRoomsByCompany = rooms;
          state.pharmacyOpeningHours = openingHours;
        },
      )
      .addCase(
        getUsersByServiceCompanyPharmacyIDData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isUpdatePharmacyLocationLoading = false;
          state.isServiceUsersByCompanyLoading = false;
          state.serviceUsersByCompany = [];
          state.serviceUsersDetailsByCompany = [];
          state.serviceRoomsByCompany = [];
          state.pharmacyOpeningHours = [];
        },
      )
      .addCase(getAvailablePharmacistsForServiceData.pending, (state) => {
        state.isUpdatePharmacyLocationLoading = true;
        state.isServiceUsersByCompanyLoading = true;
      })
      .addCase(getAvailablePharmacistsForServiceData.fulfilled, (state, action) => {
        state.isUpdatePharmacyLocationLoading = false;
        state.isServiceUsersByCompanyLoading = false;
        const payloadData = action?.payload?.response || action?.payload;
        const users = Array.isArray(payloadData)
          ? payloadData
          : payloadData?.user || [];
        const rooms = payloadData?.room || [];
        const openingHours = payloadData?.openingHours || [];
        state.serviceUsersByCompany = users;
        state.serviceUsersDetailsByCompany = users;
        state.serviceRoomsByCompany = rooms;
        state.pharmacyOpeningHours = openingHours;
      })
      .addCase(getAvailablePharmacistsForServiceData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUpdatePharmacyLocationLoading = false;
        state.isServiceUsersByCompanyLoading = false;
        state.serviceUsersByCompany = [];
        state.serviceUsersDetailsByCompany = [];
        state.serviceRoomsByCompany = [];
        state.pharmacyOpeningHours = [];
      })
      .addCase(getPharmacyAvailableSlotsData.pending, (state) => {
        state.isUpdatePharmacyLocationLoading = true;
      })
      .addCase(getPharmacyAvailableSlotsData.fulfilled, (state, action) => {
        state.isUpdatePharmacyLocationLoading = false;
        state.pharmacyAvailableSlots =
          action?.payload?.pharmacyAvailableSlots || [];
        // Normalize to YYYY-MM-DD since appointmentDate is a full ISO datetime string,
        // but callers compare it against a plain date (e.g. the date input value).
        const rawDate =
          action?.meta?.arg?.appointmentDate || action?.meta?.arg?.date || "";
        state.pharmacyAvailableSlotsDate = String(rawDate).slice(0, 10);
      })
      .addCase(getPharmacyAvailableSlotsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUpdatePharmacyLocationLoading = false;
        state.pharmacyAvailableSlots = [];
        state.pharmacyAvailableSlotsDate = "";
      })
      .addCase(getActivePharmacistsData.pending, (state) => {
        state.isServiceUsersByCompanyLoading = true;
      })
      .addCase(getActivePharmacistsData.fulfilled, (state, action) => {
        state.isServiceUsersByCompanyLoading = false;
        state.serviceUsersByCompany = Array.isArray(action?.payload)
          ? action.payload
          : action?.payload?.user || [];
      })
      .addCase(getActivePharmacistsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isServiceUsersByCompanyLoading = false;
        state.serviceUsersByCompany = [];
      })
      .addCase(getNHSEmergencyServiceData.pending, (state) => {
        state.isNHSEmergencyServiceLoading = true;
      })
      .addCase(getNHSEmergencyServiceData.fulfilled, (state, action) => {
        const raw = action?.payload || [];
        state.rawNHSEmergencyData = raw;
        state.NHSEmergencyServiceData = groupNHSEmergencyData(raw);
        state.isNHSEmergencyServiceLoading = false;
      })
      .addCase(getNHSEmergencyServiceData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isNHSEmergencyServiceLoading = false;
      })
      .addCase(getPharmacyByPharmacyIdData.fulfilled, (state, action) => {
        state.pharmacyDetails = action?.payload || [];
      })
      .addCase(getPharmacyByPharmacyIdData.pending, (state) => {})
      .addCase(getPharmacyByPharmacyIdData.rejected, (state, action) => {
        state.pharmacyDetails = [];
      })
      .addCase(getRoomsForAppointmentData.pending, (state) => {
        state.isAppointmentRoomsLoading = true;
      })
      .addCase(getRoomsForAppointmentData.fulfilled, (state, action) => {
        state.isAppointmentRoomsLoading = false;
        state.appointmentRooms = action?.payload || [];
      })
      .addCase(getRoomsForAppointmentData.rejected, (state, action) => {
        state.isAppointmentRoomsLoading = false;
        state.appointmentRooms = [];
      })
      .addCase(getPharmacyContactDetailsData.fulfilled, (state, action) => {
        state.pharmacyContactDetails = action?.payload || null;
      })
      .addCase(getPatientForNHSEmergencyServiceData.pending, (state) => {
        state.isNHSEmergencyPatientLoading = true;
      })
      .addCase(
        getPatientForNHSEmergencyServiceData.fulfilled,
        (state, action) => {
          state.NHSEmergencyPatientData = action?.payload || [];
          state.isNHSEmergencyPatientLoading = false;
        },
      )
      .addCase(
        getPatientForNHSEmergencyServiceData.rejected,
        (state, action) => {
          state.isNHSEmergencyPatientLoading = false;
        },
      )
      .addCase(updateConsultationForPatientMatchData.pending, (state) => {
        state.updateConsultationForPatientMatchLoading = true;
      })
      .addCase(
        updateConsultationForPatientMatchData.fulfilled,
        (state, action) => {
          state.updateConsultationForPatientMatchLoading = false;
          state.updateConsultationForPatientMatchResp = action?.payload || null;
        },
      )
      .addCase(
        updateConsultationForPatientMatchData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.updateConsultationForPatientMatchLoading = false;
          state.updateConsultationForPatientMatchResp = null;
        },
      )
      .addCase(updateConsultationStageData.pending, (state) => {
        state.isUpdateConsultationStageLoading = true;
      })
      .addCase(updateConsultationStageData.fulfilled, (state, action) => {
        state.isUpdateConsultationStageLoading = false;
      })
      .addCase(updateConsultationStageData.rejected, (state, action) => {
        state.isUpdateConsultationStageLoading = false;
      })
      .addCase(getReferralDataByConsultationIdData.pending, (state) => {
        state.isNHS111ReferralDataLoading = true;
      })
      .addCase(
        getReferralDataByConsultationIdData.fulfilled,
        (state, action) => {
          state.NHS111ReferralData = action?.payload || null;
          state.isNHS111ReferralDataLoading = false;

          const data = state.NHS111ReferralData;
          state.showNHS111ReferralTab =
            data !== null &&
            data !== undefined &&
            !(Array.isArray(data) && data.length === 0);
        },
      )
      .addCase(
        getReferralDataByConsultationIdData.rejected,
        (state, action) => {
          state.isNHS111ReferralDataLoading = false;
        },
      );
  },
});

// Action creators are generated for each case reducer function

export const {
  setIsPharmacyServiceLoading,
  setIsPharmacyServiceSidebarLoading,
  setIsPharmacyServicesAnalyticsLoading,
  resetPharmacyServiceState,
  setIsCalenderShow,
  resetDefaultCalenderAppointmentPharmacyServices,
  resetServiceUsersByCompany,
  setSidebarVisibleData,
  setCompletedServiceTabValueData,
  setSelectedRow,
  setServiceId,
  setCurrentServiceTabValue,
  setNHSCount,
  setPrivateCount,
  setTotalCount,
  setServiceData,
  setServiceName,
  setServiceIcon,
  setServiceBuilderType,
  setNewClinicComponent,
  setCurrentEvents,
  setServiceAnalytics,
  setCalendarStartDate,
  setCalendarEndDate,
  setCalendarDates,
  setSmsQueue,
  setNmsProgress,
  setSentToOrbitData,
  setPatientContact,
  setContactServiceId,
  setFilterPatient,
  setFilterNmsValues,
  setFilterTagsvalues,
  setFilterTagsvaluesConsented,
  setFilterNamevalues,
  setPharmacyServices,
  sortPharmacyServicesByStage,
  setIsFilterModal,
  setScrollPositionData,
  setOpenData,
  setAnalyticsTabsValueData,
  setServiceProgressData,
  setIsServiceProgressLoading,
  setOrbitQueue,
  setOrbitClinicalServiceIdData,
  setInterventionQueue,
  setDeclineQueue,
  setRescheduleQueue,
  setPdsQueue,
  setPatientNameValues,
  setInterventionStageScrollPositionData,
  setEligibilityStageScrollPositionData,
  setListViewGridScrollPositionData,
  setShowRescheduleData,
  setCheckedAllEligibleData,
  setCheckedAllFollowUpData,
  setCheckedAllInterventionData,
  setCheckedAllConsentData,
  setProcessedPDSServicesData,
  setDrugFilterValueData,
  setSendSMSAutomationData,
  setSendOrbitAutomationData,
  setSendConsentOnlyData,
  setTransformedPharmacyData,
  setSendInterventionAutomationData,
  setFilteredPharmacyServicesData,
  setOriginalPharmacyServicesData,
  setSendToInterventionOptionsData,
  setIsAutomationMenuOpenData,
  setTemplateControlValue,
  setAnyControlChecked,
  setSelectedClinicalServiceIdData,
  setShowPaymentModalData,
  setOrbitSendModal,
  setDeclineModal,
  setOrbitModalQueueData,
  setDeclineModalQueueData,
  setShowConsultationPageData,
  setServiceTypeData,
  setCurrentServieIdData,
  addDayToServiceAvailability,
  removeDayFromServiceAvailability,
  updateSlotTimes,
  addNewSlot,
  removeSlot,
  duplicateSlot,
  setCurrentCompletedServiceValue,
  isUpdatePharmacyLocationLoading,
  setShowNHSEmergencyKanbanData,
  setNHSEmergencyServiceData,
  recomputeNHSEmergencyOverdue,
  setAppointmentRoomsData,
  setMatchDialogModalData,
  setMatchPatientData,
  setIsYotiSuccess,
  setShowNHS111ReferralTabData,
} = pharmacyServiceSlice.actions;

export default pharmacyServiceSlice.reducer;
